import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../../lib/api';
import { Ayuda } from '../../../components/Ayuda';
import { Spinner } from '../../../components/Spinner';
import { useConfirm } from '../../../components/ConfirmDialog';

interface Tarifas {
  tarifas: Record<string, { in: number; out: number }>;
  fuente: string | null;
  gasto_desde: string | null;
  modelos_en_uso: { modelo: string; agentes: string[]; con_tarifa: boolean }[];
}

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : 'el inicio de los registros');

/** Tarifas por modelo (USD por millón de tokens) y contador de gasto reiniciable (Admin → Setup LLMs, 2026-10-22). */
export default function SeccionTarifas() {
  const confirmar = useConfirm();
  const [data, setData] = useState<Tarifas | null>(null);
  const [filas, setFilas] = useState<{ modelo: string; in: string; out: string }[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cargar = useCallback(async () => {
    try {
      const d = await apiFetch<Tarifas>('/api/admin/tarifas');
      setData(d);
      const modelos = new Set([...Object.keys(d.tarifas), ...d.modelos_en_uso.map((m) => m.modelo)]);
      setFilas([...modelos].sort().map((modelo) => ({ modelo, in: String(d.tarifas[modelo]?.in ?? ''), out: String(d.tarifas[modelo]?.out ?? '') })));
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudieron cargar las tarifas.'); }
  }, []);
  useEffect(() => { void cargar(); }, [cargar]);

  async function guardar() {
    setGuardando(true); setMsg(null); setError(null);
    try {
      const tarifas: Record<string, { in: number; out: number }> = {};
      for (const f of filas) { const i = Number(f.in), o = Number(f.out); if (f.modelo.trim() && Number.isFinite(i) && Number.isFinite(o) && (f.in !== '' || f.out !== '')) tarifas[f.modelo.trim()] = { in: i, out: o }; }
      setData(await apiFetch<Tarifas>('/api/admin/tarifas', { method: 'PUT', body: JSON.stringify({ tarifas }) }));
      setMsg('Tarifas guardadas. El worker las aplica en los próximos 10 minutos (los costos anteriores no se recalculan).');
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudieron guardar.'); }
    finally { setGuardando(false); }
  }
  async function reiniciar() {
    if (!(await confirmar({ titulo: 'Reiniciar el contador de gasto', mensaje: 'El Panel de Control y el reporte de consumo empezarán a sumar en USD desde este momento. No se borra ningún registro: el historial completo sigue en la base.' }))) return;
    try { setData(await apiFetch<Tarifas>('/api/admin/tarifas/reiniciar-gasto', { method: 'POST' })); setMsg('Contador reiniciado.'); }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo reiniciar.'); }
  }

  if (error && !data) return <p role="alert" className="alerta-error">{error}</p>;
  if (!data) return <div className="flex justify-center py-10"><Spinner tamano="md" /></div>;
  const sinTarifa = data.modelos_en_uso.filter((m) => !m.con_tarifa);
  return (
    <div className="flex flex-col gap-5">
      <section className="tarjeta p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-1.5 text-sm font-medium text-azul-noche">Contador de gasto en USD<Ayuda texto="El Panel de Control y scripts/consumo-llm.mjs suman el costo estimado de chats, capítulos y corridas desde esta fecha. Reiniciarlo mueve el punto de partida a ahora (útil al cambiar de proveedor o al cerrar un período); nunca borra registros." /></h2>
            <p className="mt-1 text-[12.5px] text-gris">Contando desde <span className="font-medium text-texto">{fecha(data.gasto_desde)}</span>.</p>
          </div>
          <button type="button" onClick={() => void reiniciar()} className="btn-secundario !h-9">↺ Reiniciar contador</button>
        </div>
      </section>

      <section className="tarjeta p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-1.5 text-sm font-medium text-azul-noche">Tarifas por modelo<Ayuda texto="USD por millón de tokens de entrada (in) y salida (out). Con ellas se estima el costo de cada chat, capítulo y corrida. Si cambian de Qwen a Claude, basta cargar aquí los modelos nuevos con su precio de lista; los modelos sin tarifa se registran con costo 0 y aparecen avisados." /></h2>
          <button type="button" onClick={() => void guardar()} disabled={guardando} className="btn-primario !h-9 disabled:opacity-50">{guardando ? 'Guardando…' : 'Guardar tarifas'}</button>
        </div>
        {data.fuente && <p className="mt-1 text-[11.5px] font-light text-gris">Fuente actual: {data.fuente}</p>}
        {sinTarifa.length > 0 && (
          <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 ring-1 ring-inset ring-amber-200">⚠ Sin tarifa (se registran en USD 0): {sinTarifa.map((m) => `${m.modelo} (${m.agentes.join(', ')})`).join(' · ')}</p>
        )}
        {msg && <p role="status" className="mt-3 rounded-xl bg-emerald-50/70 px-3 py-2 text-[12.5px] text-emerald-900 ring-1 ring-inset ring-emerald-200">✓ {msg}</p>}
        {error && <p role="alert" className="alerta-error mt-3">{error}</p>}
        <table className="mt-3 w-full text-[12.5px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gris"><th className="py-1.5">Modelo</th><th className="py-1.5">Lo usan</th><th className="w-28 py-1.5">USD/1M in</th><th className="w-28 py-1.5">USD/1M out</th><th className="w-8" /></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {filas.map((f, i) => {
              const uso = data.modelos_en_uso.find((m) => m.modelo === f.modelo);
              return (
                <tr key={i}>
                  <td className="py-1.5 pr-2"><input value={f.modelo} onChange={(e) => setFilas((fs) => fs.map((x, k) => (k === i ? { ...x, modelo: e.target.value } : x)))} className="campo !h-8 !text-[12.5px]" aria-label="Modelo" /></td>
                  <td className="py-1.5 pr-2 text-gris">{uso ? uso.agentes.join(', ') : <span className="font-light">—</span>}</td>
                  <td className="py-1.5 pr-2"><input value={f.in} inputMode="decimal" onChange={(e) => setFilas((fs) => fs.map((x, k) => (k === i ? { ...x, in: e.target.value } : x)))} className="campo !h-8 !text-[12.5px]" aria-label="USD por millón de tokens de entrada" /></td>
                  <td className="py-1.5 pr-2"><input value={f.out} inputMode="decimal" onChange={(e) => setFilas((fs) => fs.map((x, k) => (k === i ? { ...x, out: e.target.value } : x)))} className="campo !h-8 !text-[12.5px]" aria-label="USD por millón de tokens de salida" /></td>
                  <td className="py-1.5"><button type="button" onClick={() => setFilas((fs) => fs.filter((_, k) => k !== i))} className="text-gris hover:text-red-600" aria-label={`Quitar ${f.modelo}`}>✕</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button type="button" onClick={() => setFilas((fs) => [...fs, { modelo: '', in: '', out: '' }])} className="mt-2 text-[12.5px] font-medium text-azul-profundo hover:underline">+ Agregar modelo</button>
      </section>
    </div>
  );
}
